import { useEffect, useState } from 'react';

import { FilesFolders } from 'constants/files-folders-enum';
import { useApiUploadActions } from 'hooks/upload-actions';
import { Collapse } from 'react-bootstrap';
import { useWatch } from 'react-hook-form';

import { Accordion } from 'components/Accordion/Accordion';
import { Input, ToggleLabel } from 'components/FormComponents';
import { DocumentUpload } from 'components/FormComponents/DocumentUpload/DocumentUpload';
import { ValidationAlert } from 'components/FormComponents/ValidationAlert/ValidationAlert';
import StatusPill from 'components/Pills/StatusPills';

const settingsHeader = <span className="text-neutral-7 text-xl font-medium">Settings</span>;

export const DownloadSettings = ({ register, name, field, setValue, control, errors }) => {
  const { getUpload, signUpload, createUpload, deleteUpload } = useApiUploadActions();
  const [uploadId, setUploadId] = useState(field?.options?.uploadId || null);

  // Store upload ID instead of URL to avoid expired signed URLs
  const storedUploadId = useWatch({ control, name: `${name}.options.uploadId` });

  // Initialize uploadId from stored value
  useEffect(() => {
    if (storedUploadId !== uploadId) {
      setUploadId(storedUploadId || null);
    }
  }, [storedUploadId, uploadId]);

  const handleUploadChange = async uploadIdOrObject => {
    if (!setValue) {
      console.error('setValue is not available in DownloadSettings');
      return;
    }

    try {
      // DocumentUpload can return either an upload object or an upload ID
      // Check for null explicitly since typeof null === 'object' in JavaScript
      const uploadIdValue =
        uploadIdOrObject !== null && typeof uploadIdOrObject === 'object'
          ? uploadIdOrObject.id
          : uploadIdOrObject;

      if (uploadIdValue) {
        setUploadId(uploadIdValue);
        setValue(`${name}.options.uploadId`, uploadIdValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        // Clear the uploadId if upload is removed
        setUploadId(null);
        setValue(`${name}.options.uploadId`, null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } catch (error) {
      console.error('Error handling upload change:', error);
    }
  };

  const uploadIdError = errors?.options?.uploadId;
  const errorMessage =
    uploadIdError?.message || (uploadIdError ? 'Document upload is required' : null);

  return (
    <div className="flex flex-col overflow-hidden">
      <input
        type="hidden"
        {...register(`${name}.options.uploadId`, {
          required: 'Document upload is required',
          validate: value => {
            if (!value || value === null || value === '') {
              return 'Document upload is required';
            }
            return true;
          },
        })}
      />
      <Accordion defaultCollapsed={false} header={settingsHeader}>
        <div className="border border-neutral-4 rounded-2xl overflow-hidden">
          <div className="flex flex-col px-4 py-2 gap-2 border-b border-neutral-4">
            <span className="text-neutral-6 text-xl font-medium">Title</span>
            <Input
              register={register}
              name={`${name}.options.title`}
              placeholder="Enter title..."
            />
          </div>
          <div className="flex flex-col px-4 py-2 gap-2 border-b border-neutral-4">
            <ToggleLabel
              className="text-neutral-6 text-xl font-medium"
              register={register}
              name={`${name}.options.use_description`}
              label="Add description"
            />
            <Collapse in={field?.options?.use_description}>
              <div>
                <Input
                  register={register}
                  name={`${name}.options.description`}
                  placeholder="Enter description..."
                />
              </div>
            </Collapse>
          </div>
          <div className="flex flex-col px-4 py-2 gap-2">
            <div className="d-flex align-items-center u-gap">
              <span className="text-neutral-6 text-xl font-medium">Upload Document</span>
              {storedUploadId && <StatusPill status="success" text="Document uploaded" />}
            </div>
            {storedUploadId && (
              <a
                href="#"
                onClick={async e => {
                  e.preventDefault();
                  try {
                    const upload = await getUpload(storedUploadId);
                    if (upload?.url) {
                      window.open(upload.url, '_blank', 'noopener,noreferrer');
                    }
                  } catch (error) {
                    console.error('Error fetching upload URL:', error);
                  }
                }}
                target="_blank"
                rel="noopener noreferrer">
                View Document
              </a>
            )}
            <span className="text-neutral-6 text-xl font-medium">
              You can replace the document by uploading a new one.
            </span>
            <DocumentUpload
              value={uploadId}
              onChange={handleUploadChange}
              modelParams={{}}
              metadata={{}}
              accept={{
                'application/pdf': ['.pdf'],
                'application/msword': ['.doc', '.docx'],
                'image/*': [],
              }}
              documentTitle="Select document to upload"
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              deleteUpload={deleteUpload}
              allowDisableFormActions={false}
              bucketFolder={FilesFolders.CandidatesApplicationDocuments}
            />
            {errorMessage && (
              <div className="u-margin-top--small">
                <ValidationAlert show={true} message={errorMessage} />
              </div>
            )}
          </div>
        </div>
      </Accordion>
    </div>
  );
};
